import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ExpertButton } from "../ExpertButton";
import { ExpertBrowser } from "./ExpertBrowser";
import { useExpertSelection } from "./ExpertSelectionContext";

type InviteExpertDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const InviteExpertDialog: React.FC<InviteExpertDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const {
    groupExperts,
    selectExperts,
    linkExperts,
    unlinkExperts,
    kitExperts,
  } = useExpertSelection();
  const [selectedExpertUuids, setSelectedUuids] = useState<Set<string>>(
    new Set(),
  );
  const [unselectedExpertUuids, setUnselectedExpertUuids] = useState<
    Set<string>
  >(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const kitExpertUuids = useMemo(
    () =>
      kitExperts
        .map((e) => e.uuid)
        .reduce((set, uuid) => set.add(uuid), new Set<string>()),
    [kitExperts],
  );

  const selectedExperts = groupExperts.filter(
    (expert) => selectedExpertUuids.has(expert.uuid) === true,
  );

  const unselectedExperts = groupExperts.filter((expert) =>
    unselectedExpertUuids.has(expert.uuid),
  );

  const handleExpertAddClick = (
    selectedExpertUuids: Set<string>,
    unselectedExpertUuids: Set<string>,
  ) => {
    if (isSavingRef.current) {
      return;
    }
    setSelectedUuids(selectedExpertUuids);
    setUnselectedExpertUuids(unselectedExpertUuids);
  };

  const handleSelection = async () => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (selectedExpertUuids.size > 0) {
        await linkExperts(selectedExpertUuids);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to add experts. Please try again.");
      return;
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }

    unlinkExperts(unselectedExpertUuids);
    selectExperts(selectedExpertUuids);
    onOpenChange(false);
    setSelectedUuids(new Set());
    setUnselectedExpertUuids(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Select Experts</DialogTitle>
        </DialogHeader>
        <ExpertBrowser
          onExpertSelection={handleExpertAddClick}
          selectedExpertUuids={null}
          previousSelectedExpertUuids={kitExpertUuids}
          source="groupExperts"
          variant="cards"
          max={null}
          disabled={isSaving}
        />
        <DialogFooter className="items-start gap-4">
          <div className="flex flex-1 flex-row flex-wrap items-center gap-1">
            {unselectedExperts.map((expert) => (
              <ExpertButton
                expert={expert}
                key={expert.uuid}
                size="sm"
                className="line-through"
                disableHover
              />
            ))}
            {selectedExperts.map((expert) => (
              <ExpertButton
                expert={expert}
                key={expert.uuid}
                size="sm"
                disableHover
              />
            ))}
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleSelection}
            disabled={
              isSaving ||
              (selectedExpertUuids.size === 0 &&
                unselectedExpertUuids.size === 0)
            }
          >
            {selectedExpertUuids.size > 0 && unselectedExpertUuids.size > 0
              ? `Add ${selectedExpertUuids.size} & Remove ${unselectedExpertUuids.size} Experts`
              : selectedExpertUuids.size > 0
                ? `Add ${selectedExpertUuids.size} Experts`
                : unselectedExpertUuids.size > 0
                  ? `Remove ${unselectedExpertUuids.size} Experts`
                  : "Select Experts"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
